import { action, get } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import { capitalize } from 'frontend-utils/utils/convert-case';
import HotelAndMarketRatesOtaSelectionsRatesSelections from 'lumen/components/hotel-and-market/rates/ota-selections/rates-selections';
import {
  MAX_LOS_BOOKING,
  MAX_LOS_EXPEDIA,
  RATE_INSIGHT_FILTER_TYPE_LOS,
} from 'lumen/utils/constants/general-constants';

export default class MarketRatesOtaSelectionsMarketRatesSelections extends HotelAndMarketRatesOtaSelectionsRatesSelections {
  @service declare intl: Services['intl'];

  // May become a part of market (e.g. market.categories)?
  possibleCategories = ['market', 'brand'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get availableLos() {
    const ota = this.selectedOTA;
    let options = [...(this.market?.defaultLosRates ?? [])];
    if (ota) {
      if (ota.id === OTA_IDS.BOOKINGDOTCOM || ota.id === OTA_IDS.EXPEDIA) {
        let maxLos;
        if (ota.id === OTA_IDS.BOOKINGDOTCOM) {
          maxLos = MAX_LOS_BOOKING;
        } else if (ota.id === OTA_IDS.EXPEDIA) {
          maxLos = MAX_LOS_EXPEDIA;
        }
        // @ts-expect-error: Incorrect type
        for (let i = 1; i <= maxLos; i++) {
          options.push(i);
        }
      }
      // Addon package for extra LOS shopping on GDS
      if (ota.id === OTA_IDS.GDS) {
        options = [];
        if (this.access.hasFeature('rates.gds_default_los')) {
          options = [...options, ...(this.market?.defaultLosRates ?? [])];
        }
        if (this.access.hasFeature('rates.addons.gds_los_5_7')) {
          options.push(5);
          options.push(7);
        }
        if (this.access.hasFeature('rates.addons.gds_los_7_30')) {
          options.push(7);
          options.push(30);
        }
      }
      return [...new Set(options)]
        .sort((a, b) => a - b)
        .map((LOSOption) => ({
          id: LOSOption,
          label: this.intl.t('rates.nights', { count: LOSOption }),
          type: RATE_INSIGHT_FILTER_TYPE_LOS,
          isComparable: LOSOption !== 1,
        }));
    }
    return [
      {
        id: 1,
        label: this.intl.t('rates.nights', { count: 1 }),
        type: RATE_INSIGHT_FILTER_TYPE_LOS,
        isComparable: false,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get brandsValue() {
    // If the brands array is empty, we consider all brands selected
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.args.brands.length === 0) {
      return this.possibleBrands;
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.brands.map((brandId) =>
      // @ts-expect-error: Incorrect type
      this.possibleBrands.find((brand) => brand.id === brandId),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasOnlyOneBrand() {
    return this.market?.brands?.length === 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasOnlyOneManagementType() {
    return this.market?.managementTypes?.length === 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get managementTypesValue() {
    // If the management type array is empty, we consider all management types selected
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.args.managementTypes.length === 0) {
      return this.possibleManagementTypes;
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.managementTypes.map((managementTypeId) =>
      // @ts-expect-error: Incorrect type
      this.possibleManagementTypes.find(
        (managementType) => managementType.id === managementTypeId,
      ),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get market() {
    return this.hotelAndMarketService.market;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get onlyBrandName() {
    return this.market?.brands?.[0]?.name;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get onlyManagementTypeName() {
    // @ts-expect-error: Incorrect type
    return this.intl.t(this.market?.managementTypes?.[0]?.name);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otas() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const otas = get(this, `market.OTAs${capitalize(this.type)}`);

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return otas.slice().sort((a, b) => a.order - b.order);
  }

  // Preserve reference for isEqual check in PrismComboSelectInput
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleBrands() {
    return this.market?.brands.map((brand) => ({
      label: brand.name,
      id: brand.id,
    }));
  }

  // Preserve reference for isEqual check in PrismComboSelectInput
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get possibleManagementTypes() {
    return this.market?.managementTypes.map((managementType) => ({
      label: this.intl.t(managementType.name),
      id: managementType.id,
    }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showCompareFilterPill() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.market && this.isComparing;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getRoomtypes() {
    return this.market?.roomtypes;
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  brandsBuildLabelWithCount(count) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return this.intl.t('market.brand.count', { count });
  }

  @action
  // @ts-expect-error: Incorrect type
  chooseBrand(brands): void {
    // Selecting all brands is the same as selecting none, so we clear it
    // to avoid cluttering the URL
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (brands.length === this.possibleBrands.length) {
      // @ts-expect-error: Incorrect type
      this.args.selectionsChanged('brands', []);
      return;
    }

    // @ts-expect-error: Incorrect type
    this.args.selectionsChanged(
      'brands',
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      brands.map((brand) => brand.id),
    );
  }

  @action
  // @ts-expect-error: Incorrect type
  chooseManagementType(managementTypes): void {
    // Selecting all management types is the same as selecting none, so we clear it
    // to avoid cluttering the URL
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (managementTypes.length === this.possibleManagementTypes.length) {
      // @ts-expect-error: Incorrect type
      this.args.selectionsChanged('managementTypes', []);
      return;
    }

    // @ts-expect-error: Incorrect type
    this.args.selectionsChanged(
      'managementTypes',
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      managementTypes.map((managementType) => managementType.id),
    );
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  managementTypesBuildLabelWithCount(count) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return this.intl.t('market.management_type.count', { count });
  }
}
